import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { useContractMandateGroups } from "@rentiohq/shared-frontend/dist/hooks/useContractMandateGroups";
import * as activityActions from "@rentiohq/shared-frontend/dist/redux/activity/activity.actions";
import * as activitySelectors from "@rentiohq/shared-frontend/dist/redux/activity/activity.selectors";
import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as countSelectors from "@rentiohq/shared-frontend/dist/redux/count/count.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as bankAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.actions";
import * as bankAccountSelectors from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.selectors";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { IPagedQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import {
  ACTIVITY_SUBTYPES,
  IActivityItem,
} from "@rentiohq/shared-frontend/dist/types/activity.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { endOfDay } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getMessageForError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { useTheme, useMediaQuery } from "@rentiohq/web-shared-next/dist/themes";
import {
  Page,
  SpinningLoader,
  Stack,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { compact } from "lodash";
import hash from "object-hash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { PLANNED_COLLECTION_FILTER } from "../../utils/paymentRequest.utils";
import { DirectDebits } from "./components/DirectDebits";
import { OpenPaymentRequests } from "./components/OpenPaymentRequests";
import { PaymentHistory } from "./components/PaymentHistory";
import { PaymentHistoryTable } from "./components/PaymentHistoryTable";
import { UpcomingPaymentRequests } from "./components/UpcomingPaymentRequests";
import { EDirectDebitPresence, getDirectDebitPresence } from "./Payments.utils";

const NOW = new Date();
const END_OF_DAY = endOfDay(NOW);

export const PaymentsContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelf();

  const ibans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.getContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );

  const isFetchingIbans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.isFetchingContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );

  const ibansFetchError = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.contactBankAccountsFetchError(state, {
      contactId: user?.personalContactId,
    }),
  );

  const {
    contractGroups,
    contractsById,
    isFetchingContractGroups,
    hasFetchedContractGroups,
    contractGroupsFetchError,
  } = useContractMandateGroups(user);

  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const paymentActivitiesIdentifier = `payment-history-activities-${hash(
    getActivitiesFilter(),
  )}`;

  const [historyPage, setHistoryPage] = React.useState<number>(1);
  const [openPaymentsPage, setOpenPaymentsPage] = React.useState<number>(1);
  const [upcomingPaymentsPage, setUpcomingPaymentsPage] =
    React.useState<number>(1);

  const getOpenPaymentsFilter = () => {
    return {
      limit: CONFIG.DEFAULT_FETCH_LIMIT,
      page: openPaymentsPage,
      sort: [
        { field: "dueDateAt", method: "ASC" },
        { field: "id", method: "ASC" },
      ],
      filter: {
        requestPayerAccountId: { in: user?.accountIds },
        status: {
          in: [EPaymentRequestStatus.New, EPaymentRequestStatus.FailedPaidIn],
        },
        startedAt: { lte: NOW },
      },
    };
  };

  const getUpcomingPaymentsFilter = () => {
    return {
      limit: CONFIG.DEFAULT_FETCH_LIMIT,
      page: upcomingPaymentsPage,
      sort: [
        { field: "dueDateAt", method: "ASC" },
        { field: "id", method: "ASC" },
      ],
      filter: {
        and: [
          { requestPayerAccountId: { in: user?.accountIds } },
          {
            or: [
              {
                status: {
                  in: [
                    EPaymentRequestStatus.New,
                    EPaymentRequestStatus.FailedPaidIn,
                  ],
                },
                startedAt: { gt: END_OF_DAY },
              },
              // Already claimed for an automatic collection: upcoming until it
              // is collected, whatever its start date, and never "open" (= to
              // pay by hand).
              PLANNED_COLLECTION_FILTER,
            ],
          },
        ],
      },
    };
  };

  const {
    items: openPaymentRequests = [],
    isFetching: isFetchingOpenPaymentRequests,
    fetchError: openPaymentRequestsFetchError,
  } = paymentRequestHooks.usePaged({
    shouldRefetch: true,
    query: getOpenPaymentsFilter() as IPagedQuery,
  });

  const {
    items: upcomingPaymentRequests = [],
    isFetching: isFetchingUpcomingPaymentRequests,
    fetchError: upcomingPaymentRequestsFetchError,
  } = paymentRequestHooks.usePaged({
    shouldRefetch: true,
    query: getUpcomingPaymentsFilter() as IPagedQuery,
  });

  const { count: openPaymentRequestsCount = 0 } = paymentRequestHooks.useCount({
    shouldRefetch: true,
    query: getOpenPaymentsFilter(),
  });

  const { count: upcomingPaymentRequestsCount = 0 } =
    paymentRequestHooks.useCount({
      shouldRefetch: true,
      query: getUpcomingPaymentsFilter(),
    });

  const isFetchingActivities = useSelector((state: IRootStore) =>
    activitySelectors.isFetchingActivitiesForPage(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const fetchErrorActivities = useSelector((state: IRootStore) =>
    activitySelectors.activitiesForPageFetchError(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const activities = useSelector((state: IRootStore) =>
    activitySelectors.getActivitiesPaged(
      state,
      paymentActivitiesIdentifier,
      historyPage,
    ),
  );

  const activitiesCount = useSelector((state: IRootStore) =>
    countSelectors.getCount(state, `activities-${paymentActivitiesIdentifier}`),
  );

  // Set when this mount asks for the bank accounts. Until then an error in the
  // store belongs to an earlier visit: it must not toast again, and it must not
  // count as an answer either.
  const [didRequestIbans, setDidRequestIbans] = React.useState(false);

  React.useEffect(() => {
    if (user?.personalContactId) {
      dispatch(
        bankAccountActions.getContactBankAccountsStart.getAction({
          contactId: user.personalContactId,
        }),
      );
      setDidRequestIbans(true);
    }
  }, [user?.personalContactId]);

  // The start action clears the stored error, so after this mount's request an
  // error can only come from that request: one failure, one error, one toast.
  const currentIbansFetchError =
    didRequestIbans && !isFetchingIbans ? ibansFetchError : undefined;

  React.useEffect(() => {
    if (isFetchingActivities) {
      return;
    }

    dispatch(
      activityActions.getActivitiesPaged.actions.start({
        limit: CONFIG.DEFAULT_FETCH_LIMIT,
        page: historyPage,
        identifier: paymentActivitiesIdentifier,
        filterData: getActivitiesFilter(),
      }),
    );
  }, [historyPage, paymentActivitiesIdentifier]);

  // The bank account saga stores the raw axios error, whose message is technical
  // English ("Request failed with status code 500"). It gets the same localized
  // text ManageDirectDebits shows for this failure, so a tenant who lands on the
  // empty state knows the bank accounts could not be loaded.
  const ibansFetchErrorWithMessage = React.useMemo(
    () =>
      currentIbansFetchError
        ? { message: getMessageForError(currentIbansFetchError) }
        : undefined,
    [currentIbansFetchError],
  );

  const apiError =
    fetchErrorActivities ||
    openPaymentRequestsFetchError ||
    upcomingPaymentRequestsFetchError ||
    // Without it a failed load reads as a tenant without any direct debit.
    contractGroupsFetchError ||
    ibansFetchErrorWithMessage;

  React.useEffect(() => {
    if (apiError?.message) {
      enqueueSnackbar(apiError.message, { variant: "error" });
    }
  }, [apiError]);

  function getActivitiesFilter() {
    const sort = {
      field: "datetime",
      direction: "DESC",
    };

    return {
      order: `${sort.field} ${sort.direction}`,
      where: {
        and: compact([
          {
            subtype: {
              inq: [
                ACTIVITY_SUBTYPES.PAYMENT.PAYIN_SUCCEEDED,
                ACTIVITY_SUBTYPES.PAYMENT.PAYOUT_SUCCEEDED,
                ACTIVITY_SUBTYPES.PAYMENT.REQUEST_MANUALLY_PAID,
                ACTIVITY_SUBTYPES.PAYMENT.PAYIN_DISPUTED,
              ],
            },
          },
        ]),
      },
    };
  }

  const fetchMoreHistory = () => setHistoryPage(historyPage + 20);

  const isLoading =
    isFetchingActivities ||
    isFetchingOpenPaymentRequests ||
    isFetchingUpcomingPaymentRequests;

  // A tenant whose payments are all settled still has a direct debit to look
  // at, so the empty state is only for a tenant without any of it.
  const directDebitPresence = getDirectDebitPresence({
    contractGroupCount: contractGroups.length,
    bankAccountCount: ibans?.length ?? 0,
    isResolvingContractGroups:
      isFetchingContractGroups || !hasFetchedContractGroups,
    isResolvingBankAccounts:
      isFetchingIbans ||
      (user?.personalContactId != null &&
        ibans === undefined &&
        currentIbansFetchError == null),
  });

  // Until it is known whether the tenant has a direct debit, the page is still
  // loading. Otherwise a tenant with nothing first sees the empty cards and only
  // then the empty state.
  const isResolvingDirectDebitPresence =
    directDebitPresence === EDirectDebitPresence.Unknown;

  const isPageLoading = isLoading || isResolvingDirectDebitPresence;

  const isEmpty =
    !isPageLoading &&
    activities?.length === 0 &&
    upcomingPaymentRequestsCount === 0 &&
    openPaymentRequestsCount === 0 &&
    directDebitPresence === EDirectDebitPresence.Absent;

  return (
    <Page
      title={getLocalizedText("payments.overview.title")}
      isLoading={isPageLoading}
      isEmpty={isEmpty}
      emptyHeading={getLocalizedText("tenant.payment.empty.heading")}
      emptyMessage={getLocalizedText("tenant.payment.empty.details")}
      moreInfo={getLocalizedText("tenant.no_payment.more_info")}
    >
      {isResolvingDirectDebitPresence ? (
        <SpinningLoader />
      ) : (
        <Stack direction="column" gap={2}>
          <OpenPaymentRequests
            count={openPaymentRequestsCount}
            pageCount={Math.ceil(
              openPaymentRequestsCount / CONFIG.DEFAULT_FETCH_LIMIT,
            )}
            page={openPaymentsPage}
            paymentRequests={openPaymentRequests || []}
            isLoading={isLoading}
            handlePayNow={(id: number) => navigate(`/payment/${id}`)}
            handlePageClick={nextPage => setOpenPaymentsPage(nextPage)}
          />
          <UpcomingPaymentRequests
            count={upcomingPaymentRequestsCount}
            pageCount={Math.ceil(upcomingPaymentRequestsCount / 20)}
            page={upcomingPaymentsPage}
            isLoading={isLoading}
            paymentRequests={upcomingPaymentRequests || []}
            handlePayNow={(id: number) => navigate(`/payment/${id}`)}
            handlePageClick={nextPage => setUpcomingPaymentsPage(nextPage)}
          />
          <DirectDebits
            ibans={ibans ?? []}
            contractGroups={contractGroups}
            contractsById={contractsById}
          />
          {!downLG && activities && activities?.length > 0 && (
            <PaymentHistoryTable
              activities={activities as IActivityItem[]}
              isLoading={isFetchingActivities}
              fetchMore={fetchMoreHistory}
              activitiesCount={activitiesCount}
            />
          )}
          {downLG && activities && activities?.length > 0 && (
            <PaymentHistory
              activities={activities as IActivityItem[]}
              isLoading={isFetchingActivities}
              fetchMore={fetchMoreHistory}
              activitiesCount={activitiesCount}
            />
          )}
        </Stack>
      )}
    </Page>
  );
};
